import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Autocomplete,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  FlightTakeoff,
  Save,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import api from '../../services/api';

interface FlightFormData {
  flight_date: Date | null;
  start_time: Date | null;
  end_time: Date | null;
  duration_minutes: number | '';
  wing: string;
  wing_size: string;
  takeoff_site: string;
  landing_site: string;
  flight_type: 'solo' | 'tandem';
  passenger_name: string;
  passenger_weight: number | '';
  notes: string;
  weather_conditions: string;
  wind_direction: string;
  wind_speed: string;
  max_altitude: number | '';
  distance_km: number | '';
}

const initialFormData: FlightFormData = {
  flight_date: new Date(),
  start_time: null,
  end_time: null,
  duration_minutes: '',
  wing: '',
  wing_size: '',
  takeoff_site: '',
  landing_site: '',
  flight_type: 'solo',
  passenger_name: '',
  passenger_weight: '',
  notes: '',
  weather_conditions: '',
  wind_direction: '',
  wind_speed: '',
  max_altitude: '',
  distance_km: '',
};

const FlightForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { flightId } = useParams<{ flightId?: string }>();
  const isEditMode = !!flightId;

  const [formData, setFormData] = useState<FlightFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useDuration, setUseDuration] = useState(false);

  // Autocomplete options
  const [wingOptions, setWingOptions] = useState<string[]>([]);
  const [siteOptions, setSiteOptions] = useState<string[]>([]);

  const fetchWingsAndSites = useCallback(async () => {
    try {
      const [wingsRes, sitesRes] = await Promise.all([
        api.get('/logbook/wings'),
        api.get('/logbook/sites'),
      ]);
      setWingOptions(wingsRes.data);
      setSiteOptions(sitesRes.data);
    } catch (err) {
      console.error('Failed to fetch options:', err);
    }
  }, []);

  const fetchFlight = useCallback(async () => {
    if (!flightId) return;
    try {
      setLoading(true);
      const response = await api.get(`/logbook/flights/${flightId}`);
      const flight = response.data;

      setFormData({
        flight_date: flight.flight_date ? new Date(flight.flight_date) : null,
        start_time: flight.start_time ? parseTimeString(flight.start_time) : null,
        end_time: flight.end_time ? parseTimeString(flight.end_time) : null,
        duration_minutes: flight.duration_minutes || '',
        wing: flight.wing || '',
        wing_size: flight.wing_size || '',
        takeoff_site: flight.takeoff_site || '',
        landing_site: flight.landing_site || '',
        flight_type: flight.flight_type || 'solo',
        passenger_name: flight.passenger_name || '',
        passenger_weight: flight.passenger_weight || '',
        notes: flight.notes || '',
        weather_conditions: flight.weather_conditions || '',
        wind_direction: flight.wind_direction || '',
        wind_speed: flight.wind_speed || '',
        max_altitude: flight.max_altitude || '',
        distance_km: flight.distance_km || '',
      });

      // If duration is set but end_time is not, use duration mode
      if (flight.duration_minutes && !flight.end_time) {
        setUseDuration(true);
      }
    } catch (err) {
      console.error('Failed to fetch flight:', err);
      setError(t('logbook.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [flightId, t]);

  useEffect(() => {
    fetchWingsAndSites();
    if (isEditMode) {
      fetchFlight();
    }
  }, [isEditMode, fetchWingsAndSites, fetchFlight]);

  const parseTimeString = (timeStr: string): Date => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const formatTimeForApi = (date: Date | null): string | null => {
    if (!date) return null;
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}:00`;
  };

  const formatDateForApi = (date: Date | null): string | null => {
    if (!date) return null;
    return date.toISOString().split('T')[0];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.flight_date) {
      setError(t('logbook.dateRequired'));
      return;
    }

    if (formData.flight_type === 'tandem' && !formData.passenger_name) {
      setError(t('logbook.passengerRequired'));
      return;
    }

    try {
      setSaving(true);

      const payload: any = {
        flight_date: formatDateForApi(formData.flight_date),
        flight_type: formData.flight_type,
      };

      // Time fields
      if (formData.start_time) {
        payload.start_time = formatTimeForApi(formData.start_time);
      }
      if (!useDuration && formData.end_time) {
        payload.end_time = formatTimeForApi(formData.end_time);
      }
      if (useDuration && formData.duration_minutes) {
        payload.duration_minutes = Number(formData.duration_minutes);
      }

      // Optional string fields
      if (formData.wing) payload.wing = formData.wing;
      if (formData.wing_size) payload.wing_size = formData.wing_size;
      if (formData.takeoff_site) payload.takeoff_site = formData.takeoff_site;
      if (formData.landing_site) payload.landing_site = formData.landing_site;
      if (formData.notes) payload.notes = formData.notes;
      if (formData.weather_conditions) payload.weather_conditions = formData.weather_conditions;
      if (formData.wind_direction) payload.wind_direction = formData.wind_direction;
      if (formData.wind_speed) payload.wind_speed = formData.wind_speed;

      // Tandem fields
      if (formData.flight_type === 'tandem') {
        payload.passenger_name = formData.passenger_name;
        if (formData.passenger_weight) {
          payload.passenger_weight = Number(formData.passenger_weight);
        }
      }

      // Optional numeric fields
      if (formData.max_altitude) payload.max_altitude = Number(formData.max_altitude);
      if (formData.distance_km) payload.distance_km = Number(formData.distance_km);

      if (isEditMode) {
        await api.put(`/logbook/flights/${flightId}`, payload);
      } else {
        await api.post('/logbook/flights', payload);
      }

      navigate('/app/logbook');
    } catch (err: any) {
      console.error('Failed to save flight:', err);
      setError(err.response?.data?.detail || t('logbook.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/app/logbook')}>
            {t('logbook.back')}
          </Button>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlightTakeoff color="primary" />
            {isEditMode ? t('logbook.editFlight') : t('logbook.addFlight')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Date and Time Section */}
          <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
            {t('logbook.dateTimeSection')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
            <DatePicker
              label={t('logbook.flightDate')}
              value={formData.flight_date}
              onChange={(date) => setFormData({ ...formData, flight_date: date as Date | null })}
              slotProps={{
                textField: { required: true, sx: { minWidth: 200 } },
              }}
            />
            <TimePicker
              label={t('logbook.startTime')}
              value={formData.start_time}
              onChange={(time) => setFormData({ ...formData, start_time: time as Date | null })}
              slotProps={{
                textField: { sx: { minWidth: 150 } },
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={useDuration}
                  onChange={(e) => setUseDuration(e.target.checked)}
                />
              }
              label={t('logbook.useDuration')}
            />

            {useDuration ? (
              <TextField
                label={t('logbook.durationMinutes')}
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value ? Number(e.target.value) : '' })}
                inputProps={{ min: 1, max: 1440 }}
                sx={{ minWidth: 150 }}
              />
            ) : (
              <TimePicker
                label={t('logbook.endTime')}
                value={formData.end_time}
                onChange={(time) => setFormData({ ...formData, end_time: time as Date | null })}
                slotProps={{
                  textField: { sx: { minWidth: 150 } },
                }}
              />
            )}
          </Box>

          {/* Wing Section */}
          <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
            {t('logbook.equipmentSection')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
            <Autocomplete
              freeSolo
              options={wingOptions}
              value={formData.wing}
              onInputChange={(_, value) => setFormData({ ...formData, wing: value })}
              renderInput={(params) => (
                <TextField {...params} label={t('logbook.wing')} sx={{ minWidth: 250 }} />
              )}
            />
            <TextField
              label={t('logbook.wingSize')}
              value={formData.wing_size}
              onChange={(e) => setFormData({ ...formData, wing_size: e.target.value })}
              placeholder="S, M, L, 26..."
              sx={{ minWidth: 120 }}
            />
          </Box>

          {/* Location Section */}
          <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
            {t('logbook.locationSection')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
            <Autocomplete
              freeSolo
              options={siteOptions}
              value={formData.takeoff_site}
              onInputChange={(_, value) => setFormData({ ...formData, takeoff_site: value })}
              renderInput={(params) => (
                <TextField {...params} label={t('logbook.takeoffSite')} sx={{ minWidth: 250 }} />
              )}
            />
            <Autocomplete
              freeSolo
              options={siteOptions}
              value={formData.landing_site}
              onInputChange={(_, value) => setFormData({ ...formData, landing_site: value })}
              renderInput={(params) => (
                <TextField {...params} label={t('logbook.landingSite')} sx={{ minWidth: 250 }} />
              )}
            />
          </Box>

          {/* Flight Type Section */}
          <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
            {t('logbook.flightTypeSection')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
            <TextField
              select
              label={t('logbook.flightType')}
              value={formData.flight_type}
              onChange={(e) => setFormData({ ...formData, flight_type: e.target.value as 'solo' | 'tandem' })}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="solo">{t('logbook.solo')}</MenuItem>
              <MenuItem value="tandem">{t('logbook.tandem')}</MenuItem>
            </TextField>

            {formData.flight_type === 'tandem' && (
              <>
                <TextField
                  label={t('logbook.passengerName')}
                  value={formData.passenger_name}
                  onChange={(e) => setFormData({ ...formData, passenger_name: e.target.value })}
                  required
                  sx={{ minWidth: 200 }}
                />
                <TextField
                  label={t('logbook.passengerWeight')}
                  type="number"
                  value={formData.passenger_weight}
                  onChange={(e) => setFormData({ ...formData, passenger_weight: e.target.value ? Number(e.target.value) : '' })}
                  inputProps={{ min: 0, max: 200 }}
                  sx={{ minWidth: 150 }}
                  helperText="kg"
                />
              </>
            )}
          </Box>

          {/* Weather Section */}
          <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
            {t('logbook.weatherSection')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
            <TextField
              label={t('logbook.weatherConditions')}
              value={formData.weather_conditions}
              onChange={(e) => setFormData({ ...formData, weather_conditions: e.target.value })}
              placeholder={t('logbook.weatherPlaceholder')}
              sx={{ minWidth: 200 }}
            />
            <TextField
              label={t('logbook.windDirection')}
              value={formData.wind_direction}
              onChange={(e) => setFormData({ ...formData, wind_direction: e.target.value })}
              placeholder="N, NE, E..."
              sx={{ minWidth: 120 }}
            />
            <TextField
              label={t('logbook.windSpeed')}
              value={formData.wind_speed}
              onChange={(e) => setFormData({ ...formData, wind_speed: e.target.value })}
              placeholder="10-15 km/h"
              sx={{ minWidth: 150 }}
            />
          </Box>

          {/* Metrics Section */}
          <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
            {t('logbook.metricsSection')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
            <TextField
              label={t('logbook.maxAltitude')}
              type="number"
              value={formData.max_altitude}
              onChange={(e) => setFormData({ ...formData, max_altitude: e.target.value ? Number(e.target.value) : '' })}
              inputProps={{ min: 0 }}
              sx={{ minWidth: 150 }}
              helperText="m"
            />
            <TextField
              label={t('logbook.distance')}
              type="number"
              value={formData.distance_km}
              onChange={(e) => setFormData({ ...formData, distance_km: e.target.value ? Number(e.target.value) : '' })}
              inputProps={{ min: 0, step: 0.1 }}
              sx={{ minWidth: 150 }}
              helperText="km"
            />
          </Box>

          {/* Notes Section */}
          <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
            {t('logbook.notesSection')}
          </Typography>
          <TextField
            label={t('logbook.notes')}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            multiline
            rows={4}
            fullWidth
            placeholder={t('logbook.notesPlaceholder')}
            sx={{ mb: 4 }}
          />

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/app/logbook')}
            >
              {t('logbook.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
              disabled={saving}
            >
              {saving ? t('logbook.saving') : t('logbook.save')}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default FlightForm;
